import { useMemo } from 'react';
import { NxObject, NxObjectType } from '@warehouse/object-browser/core';
import { BrokerJob } from '@warehouse/object-browser/feature-broker';
import { JobReference, ResolvedJobReference, resolveJobReference } from '@warehouse/service-log/core';
import { serviceLogStoreSelector, useServiceLogStore } from '@warehouse/service-log/domain';

const UNKNOWN: JobReference = { kind: 'unknown' };

/**
 * What the job ran on, resolved against what the store has fetched from the Inventory so far. The job id is parsed
 * once by the store when the page loads; the cell only subscribes to its own entries.
 */
export function useJobReference(job: BrokerJob): ResolvedJobReference {
	const reference = useServiceLogStore(serviceLogStoreSelector.referenceOf(job.jobId)) ?? UNKNOWN;
	const object = useServiceLogStore(
		serviceLogStoreSelector.objectOf(reference.kind === 'object' ? reference.nxObjectUuid : undefined),
	);
	const copies = useServiceLogStore(serviceLogStoreSelector.copiesOf(contentETagOf(reference, object)));

	return useMemo(() => resolveJobReference(reference, { object, copies }), [reference, object, copies]);
}

/** The eTag whose copies the job's cells show: the job's own content, or the content of the file it ran on. */
function contentETagOf(reference: JobReference, object: NxObject | null | undefined): string | undefined {
	if (reference.kind === 'content') return reference.eTag;
	if (reference.kind === 'object' && object?.itemType === NxObjectType.File) return object.eTag;
	return undefined;
}
